import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { fpColors } from '../../styles/theme';
import { useAppSelector } from '../../store/store';
import { selectGroups } from '../sharing/groupsSlice';
import { selectUser } from '../auth/authSlice';
import { useCrossGroupSearch } from './useCrossGroupSearch';
import type { ResolvedItem } from '../../api/types';

/**
 * CrossGroupSearchScreen — Sprint 4 / Chunk 10B · FE-10.7 (Flow H).
 *
 * Unified search surface that scans every crew the caller is in
 * plus their personal inventory. Calls `GET /search/resolve` and
 * renders rows with crew + custody context so the user can answer
 * "where is the impact driver?" or "who has the laser?" in one
 * place.
 *
 * Each row shows:
 *   - thumbnail (or icon placeholder)
 *   - item name + category subtitle
 *   - crew chip strip (resolved from cached groups)
 *   - custody chip ("with you" / "with Dylan" / "due tomorrow")
 *   - tap → navigate to ItemDetail with the item id
 *
 * Empty / loading / error / no-matches states all rendered inline.
 */

export const CrossGroupSearchScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const groups = useAppSelector(selectGroups);
  const currentUser = useAppSelector(selectUser);
  const groupNameById = useMemo(() => {
    const out: Record<number, string> = {};
    for (const g of groups) out[g.id] = g.name;
    return out;
  }, [groups]);

  const search = useCrossGroupSearch();

  const handleOpenItem = (item: ResolvedItem) => {
    // Pass the first matching crew context so CustodySection picks
    // up `primaryGroupId` and surfaces the right action set.
    const groupId = item.group_ids?.[0];
    navigation.navigate('ItemDetail', {
      itemId: String(item.item_id),
      ...(groupId ? { primaryGroupId: groupId } : {}),
    });
  };

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <Pressable
          onPress={() => goBackOrHome(navigation)}
          accessibilityLabel="Back"
          accessibilityRole="button"
          style={styles.backBtn}
          hitSlop={8}
        >
          <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
        </Pressable>
        <Text style={styles.title}>Search across circles</Text>
      </View>

      <View style={styles.searchWrap}>
        <MaterialIcons name="search" size={20} color={fpColors.outline} />
        <TextInput
          value={search.query}
          onChangeText={search.setQuery}
          placeholder="What are you looking for?"
          placeholderTextColor={fpColors.outline}
          style={styles.searchInput}
          autoFocus
          autoCorrect={false}
          testID="cross-group-search-input"
        />
        {search.query ? (
          <Pressable
            onPress={() => search.setQuery('')}
            hitSlop={8}
            accessibilityLabel="Clear"
          >
            <MaterialIcons name="close" size={18} color={fpColors.outline} />
          </Pressable>
        ) : null}
      </View>

      {search.error ? (
        <View style={styles.errorBanner}>
          <MaterialIcons name="error-outline" size={18} color={fpColors.error} />
          <Text style={styles.errorText} numberOfLines={2}>
            {search.error}
          </Text>
        </View>
      ) : null}

      {!search.query.trim() ? (
        <View style={styles.emptyBox}>
          <MaterialIcons name="search" size={48} color={fpColors.outline} />
          <Text style={styles.emptyTitle}>Find anything across your circles</Text>
          <Text style={styles.emptyBody}>
            Search by item name, category, or owner. Results span every circle
            you're in plus your personal inventory.
          </Text>
        </View>
      ) : search.loading && search.items.length === 0 ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator color={fpColors.primary} />
          <Text style={styles.loadingText}>Searching…</Text>
        </View>
      ) : search.items.length === 0 ? (
        <View style={styles.emptyBox}>
          <MaterialIcons name="search-off" size={36} color={fpColors.outline} />
          <Text style={styles.emptyTitle}>No matches</Text>
          <Text style={styles.emptyBody}>
            Try a different word or category.
          </Text>
        </View>
      ) : (
        <FlatList
          data={search.items}
          keyExtractor={(it) => String(it.item_id)}
          contentContainerStyle={styles.list}
          onEndReached={search.loadMore}
          onEndReachedThreshold={0.4}
          ListHeaderComponent={
            <Text style={styles.resultHeader}>
              {search.total === 1 ? '1 match' : `${search.total} matches`}
            </Text>
          }
          ListFooterComponent={
            search.loading && search.items.length > 0 ? (
              <View style={styles.footerLoading}>
                <ActivityIndicator size="small" color={fpColors.primary} />
              </View>
            ) : !search.hasMore && search.items.length > 0 ? (
              <Text style={styles.footerEnd}>{`Showing all ${search.items.length} of ${search.total}`}</Text>
            ) : null
          }
          renderItem={({ item }) => (
            <ResultRow
              item={item}
              currentUserId={currentUser?.id ?? null}
              groupNameById={groupNameById}
              onPress={() => handleOpenItem(item)}
            />
          )}
        />
      )}
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Single result row
 * ────────────────────────────────────────────────────────────────────── */

const ResultRow: React.FC<{
  item: ResolvedItem;
  currentUserId: number | null;
  groupNameById: Record<number, string>;
  onPress: () => void;
}> = ({ item, currentUserId, groupNameById, onPress }) => {
  const custodianLabel = useMemo(() => {
    if (!item.current_custodian_user_id) return null;
    if (currentUserId && item.current_custodian_user_id === currentUserId) {
      return { text: 'You have', tone: 'self' as const };
    }
    if (item.current_custodian_user_id === item.owner_user_id) {
      return null; // default — item with owner, no chip needed
    }
    const name = item.custodian_name?.trim() || 'Out on loan';
    let overdue = false;
    if (item.custody_due_date) {
      const t = new Date(item.custody_due_date).getTime();
      if (Number.isFinite(t) && t < Date.now()) overdue = true;
    }
    return { text: overdue ? `${name} ⚠` : name, tone: overdue ? 'overdue' as const : 'other' as const };
  }, [item, currentUserId]);

  const photo = item.primary_photo_url;
  const crewChips = (item.group_ids ?? [])
    .map((id) => groupNameById[id])
    .filter(Boolean)
    .slice(0, 2);

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Open ${item.item_name ?? 'item'}`}
      style={({ pressed }) => [
        styles.row,
        pressed && { opacity: 0.85 },
      ]}
      testID={`resolve-result-${item.item_id}`}
    >
      {photo ? (
        <Image
          source={{ uri: photo }}
          style={styles.thumb}
          accessibilityIgnoresInvertColors
        />
      ) : (
        <View style={[styles.thumb, styles.thumbPlaceholder]}>
          <MaterialIcons name="image" size={22} color={fpColors.outline} />
        </View>
      )}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.itemName} numberOfLines={1}>
          {item.item_name ?? 'Unnamed item'}
        </Text>
        {item.item_category ? (
          <Text style={styles.itemCategory} numberOfLines={1}>
            {item.item_category}
          </Text>
        ) : null}
        <View style={styles.chipRow}>
          {crewChips.map((name) => (
            <View key={name} style={styles.crewChip}>
              <MaterialIcons
                name="group"
                size={11}
                color={fpColors.primary}
              />
              <Text style={styles.crewChipText} numberOfLines={1}>
                {name}
              </Text>
            </View>
          ))}
          {custodianLabel ? (
            <View
              style={[
                styles.custodyChip,
                custodianLabel.tone === 'self' && styles.custodyChipSelf,
                custodianLabel.tone === 'overdue' && styles.custodyChipOverdue,
              ]}
            >
              <MaterialIcons
                name={
                  custodianLabel.tone === 'overdue'
                    ? 'priority-high'
                    : custodianLabel.tone === 'self'
                    ? 'person'
                    : 'person-pin'
                }
                size={11}
                color="#FFFFFF"
              />
              <Text style={styles.custodyChipText} numberOfLines={1}>
                {custodianLabel.text}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
      <MaterialIcons
        name="chevron-right"
        size={22}
        color={fpColors.outline}
      />
    </Pressable>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: fpColors.background,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  backBtn: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 18,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 16,
    marginTop: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 10,
  },
  searchInput: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 15,
    color: fpColors.onSurface,
    padding: 0,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: fpColors.errorContainer,
    borderRadius: 10,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.error,
  },
  loadingBox: {
    paddingTop: 60,
    alignItems: 'center',
    gap: 10,
  },
  loadingText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
  },
  emptyBox: {
    paddingTop: 80,
    paddingHorizontal: 40,
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
    marginTop: 12,
    textAlign: 'center',
  },
  emptyBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
    marginTop: 6,
    textAlign: 'center',
    lineHeight: 18,
  },
  list: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 24,
  },
  resultHeader: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.outline,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    marginBottom: 8,
  },
  thumb: {
    width: 56,
    height: 56,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainer,
  },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  itemCategory: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
    marginTop: 2,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 6,
  },
  crewChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
  },
  crewChipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    color: fpColors.primary,
    maxWidth: 120,
  },
  custodyChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: 'rgba(28, 23, 33, 0.78)',
  },
  custodyChipSelf: {
    backgroundColor: 'rgba(125, 52, 183, 0.92)',
  },
  custodyChipOverdue: {
    backgroundColor: 'rgba(186, 26, 26, 0.92)',
  },
  custodyChipText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: '#FFFFFF',
    maxWidth: 100,
  },
  footerLoading: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  footerEnd: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
    textAlign: 'center',
    paddingVertical: 14,
  },
});

export default CrossGroupSearchScreen;
